import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { styled, css } from '@mui/material/styles';
import React from 'react';

import TaskCommentDrawer from './task-comment-drawer';

import Button from '~/client/components/base/button';
import Chip from '~/client/components/base/chip';
import { getDesignTokens, Color } from '~/client/components/base/core';
import {
  GenericTableNew,
  TableColumnNew as TableColumn,
} from '~/client/components/base/generic-table';
import { ArrowRight } from '~/client/components/base/icon';
import Text from '~/client/components/base/text';
import {
  aiSystemAssessmentReportRouteUtil,
  functionalModelAssessmentReportRouteUtil,
  candidateModelAssessmentReportRouteUtil,
  aiDashboardUtils,
  functionalModelDashboardUtils,
  candidateModelDashboardUtils,
} from '~/client/routes';
import { formatAssignees, getStatusLabel } from '~/client/utils/task-helpers';
import * as GQL from '~/graphql/generated/client';
interface TaskTableViewProps {
  tasks: GQL.UserTask[];
  onRowClick?: (task: GQL.UserTask) => void;
  className?: string;
  isArchived?: boolean;
}

const TaskTableViewImpl: React.FC<TaskTableViewProps> = ({
  tasks,
  onRowClick,
  className,
  isArchived = false,
}) => {
  const [commentDrawerOpen, setCommentDrawerOpen] = React.useState(false);
  const [selectedTask, setSelectedTask] = React.useState<GQL.UserTask | null>(
    null
  );
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const aiSystemReportHelper = aiSystemAssessmentReportRouteUtil.useHelper();
  const functionalModelReportHelper =
    functionalModelAssessmentReportRouteUtil.useHelper();
  const candidateModelReportHelper =
    candidateModelAssessmentReportRouteUtil.useHelper();

  const aiDashboard = aiDashboardUtils.useHelper();
  const functionalModelDashboard = functionalModelDashboardUtils.useHelper();
  const candidateModelDashboard = candidateModelDashboardUtils.useHelper();

  const getCommentCount = (task: GQL.UserTask) => task.commentCount || 0;

  const handleCommentClick = (e: React.MouseEvent, task: GQL.UserTask) => {
    e.stopPropagation();
    setSelectedTask(task);
    setCommentDrawerOpen(true);
  };

  const handleRowClick = (task: GQL.UserTask) => {
    // Archived tasks are read-only audit trail - no navigation
    if (isArchived) {
      return;
    }

    if (onRowClick) {
      onRowClick(task);
      return;
    }

    try {
      switch (task.projectType) {
        case GQL.ProjectType.Organization:
        case GQL.ProjectType.AiSystem:
          if (task.assignmentType === GQL.AssignmentType.Submission) {
            return aiDashboard.navigateTo('DASHBOARD', {
              params: {
                projectId: task.projectId,
              },
            });
          }
          aiSystemReportHelper.navigateTo('ASSESSMENT_REPORT', {
            params: {
              projectId: task.projectId,
              assessmentId: task.assessmentId,
            },
            state: {
              from: 'tasks',
            },
          });
          break;

        case GQL.ProjectType.FunctionalModel:
          if (task.parentAISystem) {
            if (task.assignmentType === GQL.AssignmentType.Submission) {
              return functionalModelDashboard.navigateTo('DASHBOARD', {
                params: {
                  projectId: task.parentAISystem,
                  functionalModelId: task.projectId,
                },
              });
            }
            functionalModelReportHelper.navigateTo('ASSESSMENT_REPORT', {
              params: {
                projectId: task.parentAISystem,
                functionalModelId: task.projectId,
                assessmentId: task.assessmentId,
              },
              state: {
                from: 'tasks',
              },
            });
          }
          break;

        case GQL.ProjectType.ModelCandidate:
        case GQL.ProjectType.ModelChampion:
        case GQL.ProjectType.Dataset:
          if (task.parentAISystem && task.functionalModel) {
            if (task.assignmentType === GQL.AssignmentType.Submission) {
              return candidateModelDashboard.navigateTo('CANDIDATE_DETAILS', {
                params: {
                  projectId: task.parentAISystem,
                  functionalModelId: task.functionalModel,
                  candidateId: task.projectId,
                },
              });
            }
            candidateModelReportHelper.navigateTo('ASSESSMENT_REPORT', {
              params: {
                projectId: task.parentAISystem,
                functionalModelId: task.functionalModel,
                candidateId: task.projectId,
                assessmentId: task.assessmentId,
              },
              state: {
                from: 'tasks',
              },
            });
          }
          break;
      }
    } catch (err) {
      console.error('Error navigating to task details:', err);
    }
  };

  React.useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Base columns – ordered to match the design: Task → Project → Type → Assigned To
  const baseColumns: TableColumn<GQL.UserTask>[] = [
    {
      key: 'policyName',
      label: (
        <Text sz="body14" color="secondary">
          Task
        </Text>
      ),
      width: '28%',
      render: (_, task) => <Text sz="body14">{task.policyName}</Text>,
      cellClassName: 'table-cell table-cell-w-28',
    },
    {
      key: 'projectName',
      label: (
        <Text sz="body14" color="secondary">
          Project
        </Text>
      ),
      width: '20%',
      render: (_, task) => <Text sz="body14">{task.projectName}</Text>,
      cellClassName: 'table-cell table-cell-w-20',
    },
    {
      key: 'assignmentType',
      label: (
        <Text sz="body14" color="secondary">
          Type
        </Text>
      ),
      width: '10%',
      render: (_, task) => (
        <Text sz="body14" sx={{ textTransform: 'capitalize' }}>
          {task.assignmentType.toLowerCase()}
        </Text>
      ),
      cellClassName: 'table-cell table-cell-w-10',
    },
    {
      key: 'assignedTo',
      label: (
        <Text sz="body14" color="secondary">
          Assigned To
        </Text>
      ),
      width: '14%',
      render: (_, task) => (
        <Text sz="body14">{formatAssignees(task.assignees || [])}</Text>
      ),
      cellClassName: 'table-cell table-cell-w-14',
    },
    {
      key: 'comments',
      align: 'center',
      label: (
        <Text sz="body14" color="secondary">
          Comments
        </Text>
      ),
      width: '6%',
      render: (_, task) => {
        const count = getCommentCount(task);
        return (
          <IconButton
            size="small"
            onClick={(e) => handleCommentClick(e, task)}
            className="comment-btn"
          >
            <Badge
              badgeContent={count}
              color="primary"
              invisible={
                count === 0 ||
                (isArchived
                  ? (task.overallOutcome ?? task.status) ===
                    GQL.AssignmentStatusType.Pending
                  : task.status === GQL.AssignmentStatusType.Pending)
              }
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.65rem',
                  height: '16px',
                  minWidth: '16px',
                  color: 'white',
                },
              }}
            >
              <ChatBubbleOutlineIcon fontSize="small" />
            </Badge>
          </IconButton>
        );
      },
      cellClassName: 'table-cell table-cell-w-6 table-cell-center',
    },
  ];

  // Columns for active tasks (existing behaviour)
  const activeColumns: TableColumn<GQL.UserTask>[] = [
    ...baseColumns,
    {
      key: 'status',
      label: (
        <Text sz="body14" color="secondary">
          Status
        </Text>
      ),
      width: '10%',
      align: 'center',
      render: (_, task) => (
        <Chip
          className="status-chip"
          data-status={task.status}
          label={<Text sz="body14">{getStatusLabel(task.status)}</Text>}
          variant="filled"
          size="small"
        />
      ),
      cellClassName: 'table-cell table-cell-w-10',
    },
    {
      key: 'dueDate',
      label: (
        <Text sz="body14" color="secondary">
          Due Date
        </Text>
      ),
      width: '10%',
      render: (_, task) => (
        <Text sz="body14">{new Date(task.dueDate).toLocaleDateString()}</Text>
      ),
      cellClassName: 'table-cell table-cell-w-10',
    },
    {
      key: 'actions',
      label: '',
      width: '4%',
      align: 'right',
      render: () => <Button icon={ArrowRight} />,
      cellClassName: 'table-cell table-cell-w-4',
    },
  ];

  // Columns for archived tasks – shows Outcome instead of Status
  const archivedColumns: TableColumn<GQL.UserTask>[] = [
    ...baseColumns,
    {
      key: 'outcome',
      label: (
        <Text sz="body14" color="secondary">
          Outcome
        </Text>
      ),
      width: '10%',
      render: (_, task) => (
        <Chip
          className="outcome-chip"
          data-outcome={task.overallOutcome ?? task.status}
          label={
            <Text sz="body14">
              {getStatusLabel(task.overallOutcome ?? task.status)}
            </Text>
          }
          variant="filled"
          size="small"
        />
      ),
      cellClassName: 'table-cell table-cell-w-10',
    },
    {
      key: 'archivedAt',
      label: (
        <Text sz="body14" color="secondary">
          Archived
        </Text>
      ),
      width: '10%',
      render: (_, task) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CalendarTodayOutlinedIcon
            sx={{ fontSize: 13, opacity: 0.5, flexShrink: 0 }}
          />
          <Text sz="body14">
            {task.archivedAt
              ? new Date(task.archivedAt).toLocaleDateString()
              : '-'}
          </Text>
        </Box>
      ),
      cellClassName: 'table-cell table-cell-w-10',
    },
  ];

  const columns = isArchived ? archivedColumns : activeColumns;

  return (
    <>
      <div className={className}>
        <GenericTableNew
          columns={columns}
          data={tasks}
          getRowKey={(task) => task.taskId}
          onRowClick={handleRowClick}
          rowClassName="table-row"
          className="table"
          usePaper={false}
          containerClassName=""
          showColumnBorders={false}
          showRowBorders
          headerBackground="light"
        />
      </div>
      <TaskCommentDrawer
        open={commentDrawerOpen}
        onClose={() => {
          setCommentDrawerOpen(false);
          // Delay clearing task to prevent content flash during close animation
          setTimeout(() => setSelectedTask(null), 300);
        }}
        task={selectedTask}
      />
    </>
  );
};

const TaskTableView = styled(TaskTableViewImpl)(({ theme }) => {
  const t = getDesignTokens(theme.palette.mode, theme.accentPalette);
  return css`
    & .MuiChip-label {
      text-transform: capitalize;
    }

    .task-content {
      padding: 0;
    }

    .type-chip {
      background-color: ${t.accentBg};
      color: ${t.accent};
    }

    .version-chip {
      background-color: ${t.accentBg};
      color: ${t.accent};
    }

    .status-chip {
      &[data-status='PENDING'] {
        background-color: ${t.surface2};
        color: ${t.textPrimary};
      }
      &[data-status='COMPLETED'] {
        background-color: ${theme.palette.success.main};
        color: ${t.status.success};
      }
      &[data-status='REJECTED'] {
        background-color: ${t.risk.highBg};
        color: ${t.status.danger};
      }
    }

    .outcome-chip {
      background-color: ${t.surface2};
      color: ${t.textPrimary};
      text-transform: capitalize;

      &[data-outcome='REJECTED'] {
        background-color: ${t.risk.highBg};
        color: ${t.status.danger};
      }
      &[data-outcome='COMPLETED'] {
        background-color: ${theme.palette.success.main};
        color: ${t.status.success};
      }
    }

    .outcome-chip {
      background-color: ${Color.lightLight};
      color: ${Color.black};
      text-transform: capitalize;

      &[data-outcome='REJECTED'] {
        background-color: ${Color.lightRed};
        color: ${Color.red};
      }
      &[data-outcome='APPROVED'] {
        background-color: ${theme.palette.success.main};
        color: ${Color.green};
      }
    }

    .table {
      width: 100%;
      table-layout: fixed;
      border-collapse: collapse;
      margin: 0;
      border-spacing: 0;
    }

    ${theme.breakpoints.down('md')} {
      min-width: 1000px;
    }

    ${theme.breakpoints.up('md')} {
      min-width: auto;
    }

    .table-cell {
      padding: ${theme.spacing(3)};
      white-space: normal;
      word-wrap: break-word;
      vertical-align: middle;
      /* Border will be handled by generic-table-new */
    }

    .table-cell-w-28 {
      width: 28%;
      padding-left: ${theme.spacing(3)};
    }
    .table-cell-w-20 {
      width: 20%;
    }
    .table-cell-w-14 {
      width: 14%;
    }
    .table-cell-w-12 {
      width: 12%;
    }
    .table-cell-w-10 {
      width: 10%;
    }
    .table-cell-w-8 {
      width: 8%;
    }
    .table-cell-w-6 {
      width: 6%;
    }
    .table-cell-w-4 {
      width: 4%;
    }
    .comment-btn {
      color: ${t.iconInteractive};
      &:hover {
        background-color: ${t.surface2};
      }
    }
    .table-cell-center {
      text-align: center;
    }
    .table-row {
      cursor: pointer;
      &:hover {
        background-color: ${t.surface2};
      }
    }
  `;
});

export default TaskTableView;
